import { useState, useMemo, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import TrueWellnessHeader from "@/components/truewellness/TrueWellnessHeader";
import TrueWellnessFooter from "@/components/truewellness/TrueWellnessFooter";
import LocationCard from "@/components/locations/LocationCard";
import LocationsWaitlist from "@/components/locations/LocationsWaitlist";
import LocationsMap from "@/components/locations/LocationsMap";
import LearnMoreModal from "@/components/truewellness/LearnMoreModal";

const Locations = () => {
  const [selectedState, setSelectedState] = useState<string>("all");
  const [selectedLocationId, setSelectedLocationId] = useState<string>();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const cardRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  // Fetch locations from database
  const { data: dbLocations = [], isLoading } = useQuery({
    queryKey: ['locations'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('locations')
        .select('*')
        .order('state', { ascending: true })
        .order('city', { ascending: true });
      
      if (error) throw error;
      return data || [];
    },
  });

  // Get unique states from database locations
  const states = useMemo(() => {
    const stateSet = new Set(dbLocations.map(loc => loc.state));
    return Array.from(stateSet).sort();
  }, [dbLocations]);

  const filteredLocations = useMemo(() => {
    // Hide Gemini Glow temporarily
    let filtered = dbLocations.filter((loc) => !loc.name.toLowerCase().includes('gemini'));
    
    if (selectedState !== "all") {
      filtered = filtered.filter((loc) => loc.state === selectedState);
    }

    return filtered;
  }, [selectedState, dbLocations]);

  const handleLocationSelect = (locationId: string) => {
    setSelectedLocationId(locationId);
    
    // Scroll to the card
    const cardElement = cardRefs.current.get(locationId);
    if (cardElement) {
      cardElement.scrollIntoView({ behavior: "smooth", block: "center" });
    }

    // Clear highlight after 3 seconds
    setTimeout(() => {
      setSelectedLocationId(undefined);
    }, 3000);
  };

  const setCardRef = (locationId: string, element: HTMLDivElement | null) => {
    if (element) {
      cardRefs.current.set(locationId, element);
    } else {
      cardRefs.current.delete(locationId);
    }
  };

  return (
    <>
      <Helmet>
        <title>Find a Provider | TrueWellness Locations</title>
        <meta
          name="description"
          content="Find a board-certified TrueWellness provider near you. Browse our network of aesthetic professionals offering wellness services across the United States."
        />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-[#0D1B2A] via-[#1B263B] to-[#0D1B2A]">
        <TrueWellnessHeader onOpenModal={() => setIsModalOpen(true)} />

        {/* Hero Section */}
        <section className="pt-28 pb-8 md:pt-36 md:pb-12">
          <div className="container mx-auto px-4 text-center">
            <h1 className="text-4xl md:text-6xl font-black mb-4 text-white">
              Find a <span className="text-[#ffdb4d]">True</span> Provider
            </h1>
            <p className="text-lg md:text-xl text-white/70 max-w-2xl mx-auto">
              Every True Provider is a licensed, board-certified medical professional meeting the highest clinical standards.
            </p>
          </div>
        </section>

        {/* Filter Section */}
        <section className="py-4">
          <div className="container mx-auto px-4">
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-white">Filter by State</span>
              <Select value={selectedState} onValueChange={setSelectedState}>
                <SelectTrigger className="w-48 bg-white/10 border-white/20 text-white">
                  <SelectValue placeholder="All States" />
                </SelectTrigger>
                <SelectContent className="bg-[#1B263B] border-white/20">
                  <SelectItem value="all" className="text-white hover:bg-white/10">All States</SelectItem>
                  {states.map((state) => (
                    <SelectItem key={state} value={state} className="text-white hover:bg-white/10">
                      {state}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!isLoading && (
                <span className="text-sm text-white/60">
                  {filteredLocations.length} provider{filteredLocations.length !== 1 ? 's' : ''}
                </span>
              )}
            </div>
          </div>
        </section>

        {/* Provider Cards Grid */}
        <section className="py-12">
          <div className="container mx-auto px-4">
            {isLoading ? (
              <div className="text-center py-12">
                <p className="text-white/60">Loading providers...</p>
              </div>
            ) : filteredLocations.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-white/60">No providers found in this area.</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredLocations.map((location) => (
                  <LocationCard 
                    key={location.id} 
                    ref={(el) => setCardRef(location.id, el)}
                    location={location} 
                    onOpenModal={() => setIsModalOpen(true)}
                    isHighlighted={selectedLocationId === location.id}
                  />
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Waitlist Section */}
        <LocationsWaitlist />

        <TrueWellnessFooter />
        
        <LearnMoreModal open={isModalOpen} onOpenChange={setIsModalOpen} />
      </div>
    </>
  );
};

export default Locations;